import { render } from 'preact-render-to-string';
import { BasicEmailWrapper, Heading, Line, SupportFooter, CenteredLine } from './shared';
import {
  hasUnrenderableMedia,
  resolveSubmissionFiles,
  sanitizeSubmissionHtml,
} from 'server/lib/xss';

/**
 * A single question in a lesson's assignment, paired with the student's answer.
 */
export type AssignmentAnswerItem = {
  questionId: UUID;
  /** The question prompt, as written by the guide. May be empty. */
  question: string;
  /** The student's answer, as HTML. Sanitized before it is rendered. */
  content: string;
};

type Props = {
  course: { id: UUID; title: string };
  lessonTitle: string;
  studentName: string;
  answers: AssignmentAnswerItem[];
  /** Deep link to this student's submission in the guide's review screen */
  reviewUrl: string;
  /** Link to the lesson's assignment settings, for notification preferences */
  lessonUrl: string;
  /** Origin the tenant serves files from, e.g. `https://playground.mirasee.com` */
  fileBaseUrl: string;
  /** Uploads readable without a login, so an inbox can actually fetch them */
  publicFileIds: Set<string>;
};

/**
 * Gmail clips a message once it passes ~102KB, hiding everything below the cut
 * behind a "view entire message" link — including, in this email, the link a
 * guide needs to give feedback. Answers are capped well short of that so the
 * whole email stays visible.
 */
const ANSWER_CHARACTER_LIMIT = 5000;

/**
 * The character cap counts text, so it can't see markup. One legitimate image
 * URL pasted two thousand times is a 180KB email of almost no text — past the
 * clip, with the review link below the fold. This cap counts what actually gets
 * sent.
 */
const ANSWER_BYTE_LIMIT = 40000;

/**
 * Guide notification for one student's assignment submission. The submitted
 * answers are included in full so coaches can read (and reply to) the work
 * without leaving their inbox.
 */
export function AssignmentSubmitted({
  course,
  lessonTitle,
  studentName,
  answers,
  reviewUrl,
  lessonUrl,
  fileBaseUrl,
  publicFileIds,
}: Props) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter />}>
      <Heading>
        {studentName} submitted {lessonTitle}
      </Heading>
      <Line style="color:#636363;">
        in {course.title} &middot; <a href={reviewUrl}>review and give feedback</a>
      </Line>

      {answers.map((answer) => (
        <AnswerBlock
          key={answer.questionId}
          {...answer}
          reviewUrl={reviewUrl}
          fileBaseUrl={fileBaseUrl}
          publicFileIds={publicFileIds}
        />
      ))}

      <Line>
        <a href={reviewUrl}>Review and give feedback</a>
      </Line>

      <CenteredLine>
        <span style="font-size: 0.75rem;color:#636363">
          <a href={lessonUrl}>Manage notification settings for {lessonTitle}</a>
        </span>
      </CenteredLine>
    </BasicEmailWrapper>,
  );
}

/**
 * One question prompt followed by the student's answer.
 */
function AnswerBlock({
  question,
  content,
  reviewUrl,
  fileBaseUrl,
  publicFileIds,
}: AssignmentAnswerItem & {
  reviewUrl: string;
  fileBaseUrl: string;
  publicFileIds: Set<string>;
}) {
  const sanitized = sanitizeSubmissionHtml(content, { fileOrigin: fileBaseUrl });
  const resolved = resolveSubmissionFiles(sanitized, { baseUrl: fileBaseUrl, publicFileIds });
  // Truncating sanitized HTML can leave tags unclosed, so cut the plain text and
  // let the mail client render that instead of half a markup tree. Measure the
  // text too — gating on markup length would flatten a short but heavily
  // formatted answer and label it "too long".
  const text = stripTags(resolved.html);
  const characters = Array.from(text);
  const isTruncated =
    characters.length > ANSWER_CHARACTER_LIMIT ||
    Buffer.byteLength(resolved.html) > ANSWER_BYTE_LIMIT;
  const html = isTruncated ? `${trimToWholeWord(characters)}&hellip;` : resolved.html;
  const hasHiddenContent = hasUnrenderableMedia(content) || resolved.hiddenCount > 0;

  return (
    <div style="margin-top:1.5rem;margin-bottom:1.5rem;">
      {!!question && <p style="margin-bottom:0.5rem;font-weight:600;color:#3f3f46;">{question}</p>}
      <div
        style="padding:16px;background-color:#f8fafc;border-left:4px solid #5850ec;color:#3f3f46;overflow-wrap:break-word;word-break:break-word;"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {isTruncated && (
        <p style="margin-top:0.5rem;font-size:0.85rem;color:#636363;">
          This answer is too long to show in full. <a href={reviewUrl}>Open the full submission</a>{' '}
          to read the rest.
        </p>
      )}
      {hasHiddenContent && (
        <p style="margin-top:0.5rem;font-size:0.85rem;color:#636363;">
          This submission includes images, attachments or media that can&rsquo;t be shown in email.{' '}
          <a href={reviewUrl}>Open the full submission</a> to see them.
        </p>
      )}
    </div>
  );
}

/**
 * Drops tags from already-sanitized markup. Entities are left alone so text the
 * student typed as `<` stays escaped rather than becoming markup again. Adjacent
 * tags get a space between them first, so paragraphs and list items don't run
 * together into `Item 0Item 1`.
 */
function stripTags(html: string) {
  return html.replace(/></g, '> <').replace(/<[^>]*>/g, '');
}

/**
 * Cuts to the character limit on a word boundary. Slicing an array of code
 * points rather than the string keeps an emoji from being split down the middle
 * into an unpaired surrogate, and dropping a trailing partial entity keeps
 * `&amp;` from arriving as a bare `&am`.
 *
 * The word boundary only applies near the end of the cut. Text pasted from Word
 * or Google Docs separates words with `&nbsp;` entities rather than spaces, so
 * the last real space can be thousands of characters back — trimming to it would
 * throw away almost the whole answer under a notice that says it was merely
 * shortened.
 */
function trimToWholeWord(characters: string[]) {
  const cut = characters.slice(0, ANSWER_CHARACTER_LIMIT).join('');
  const lastSpace = cut.lastIndexOf(' ');
  const floor = cut.length - 100;
  return (lastSpace > floor ? cut.slice(0, lastSpace) : cut).replace(/&[#a-zA-Z0-9]*$/, '');
}
